import { State, Selector, Action, StateContext, Store, Select } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { PushService } from 'src/app/services/push.service';
import { RecaptchaHeader } from 'src/app/services/recaptcha.service';
import { LogLevel, Utility } from '../core/common';
import { SharedService } from '../services/shared.service';

export class AuthStateModel {
	token?: string;
	user?: any;
	guestId?: string;
	confirmCode?: string;
}

export class Current {
	static readonly type = '[Auth] Current';
	constructor(public token: string = null) { }
}

export class Update {
	static readonly type = '[Auth] Update';
	constructor(public user: any, public apiCall = true) { }
}

export class Login {
	static readonly type = '[Auth] Login';
	constructor(public username: string, public password: string, public headers?: RecaptchaHeader) { }
}

export class ConfirmAuthentication {
	static readonly type = '[Auth] Confirm Authentication';
	constructor(public mobile: string, public code: string, public info?: any, public headers?: RecaptchaHeader) { }
}

export class CheckConfirmAuthentication {
	static readonly type = '[Auth] Check Confirm Authentication';
	constructor(public mobile: string, public code: string, public info?: any) { }
}

export class DaanAuthentication {
	static readonly type = '[Auth] Daan Authentication';
	constructor(public payload: string) { }
}

export class Logout {
	static readonly type = '[Auth] Logout';
}

@State<AuthStateModel>({
	name: 'auth'
})
@Injectable()
export class AuthState {
	constructor(private authService: AuthService, private store: Store, private pushService: PushService, private sharedService: SharedService) { }

	@Selector()
	static user(state: AuthStateModel) { return state.user; }

	@Select(AuthState.user) static user$: Observable<any>;

	@Action(Current)
	async current(context: StateContext<AuthStateModel>, payload: Current) {
		const token = payload?.token || this.store.selectSnapshot(state => state.auth.token);

		context.patchState({ token: token });
		let user: any = null;
		if (token) user = (await this.authService.current()).body;
		context.patchState({ user: user });

		return user;
	}

	@Action(Update)
	async update(context: StateContext<AuthStateModel>, payload: Update) {
		const current = this.store.selectSnapshot(state => state.auth.user);
		if(current && current.Id != payload.user.Id) 
			throw new Error('Invalid user update');
		if(payload.apiCall) {
			const resp = await this.authService.update(payload.user || current);
			context.patchState({ user: resp.body });
		} else {
			context.patchState({ user: Utility.clone(payload.user) });
		}

	}

	@Action(Login)
	async login(context: StateContext<AuthStateModel>, payload: Login) {
		const resp = await this.authService.login(payload.username, payload.password, payload.headers);
		context.patchState({ token: resp.headers.get('Authorization'), user: resp.body });
		await this.pushService.registerPush();
	}


	static readonly userChangedMessageType = 'user-changed';
	static readonly connectHubType = 'connect-hub';

	@Action(ConfirmAuthentication)
	async confirmAuthentication(context: StateContext<AuthStateModel>, payload: ConfirmAuthentication) {
		try {
			const resp = await this.authService.confirm(payload.mobile, payload.code, payload.info, payload.headers);
			const user = resp.body;
			if (user) {
				const token = resp.headers.get('Authorization');
				context.patchState({ token: token, user });
				await this.pushService.registerPush();
				this.pushService.postBroadcastMessage({ type: AuthState.userChangedMessageType, payload: { token } });
			}
		} catch (error) {
			this.sharedService.log(LogLevel.Error, 'AuthState.confirmAuthentication::error', null, {payload, error});
		}
	}

	@Action(CheckConfirmAuthentication)
	async checkConfirmAuthentication(context: StateContext<AuthStateModel>, payload: CheckConfirmAuthentication) {
		const resp = await this.authService.checkConfirm(payload.mobile, payload.code, payload.info);
		const {User: user = undefined, Code: code = undefined} = resp.body;
		if(code)
			context.patchState({ confirmCode: code });
		
		if (user) {
			const token = resp.headers.get('Authorization');
			context.patchState({ token: token, user });
			await this.pushService.registerPush();
			this.pushService.postBroadcastMessage({ type: AuthState.userChangedMessageType, payload: { token } });
		}
	}

	@Action(DaanAuthentication)
	async daanAuthentication(context: StateContext<AuthStateModel>, payload: DaanAuthentication) {
		const resp = await this.authService.daanAuthenticate(payload.payload);
		context.patchState({ token: resp.headers.get('Authorization'), user: resp.body });
		await this.pushService.registerPush();
	}

	@Action(Logout)
	async logout(context: StateContext<AuthStateModel>) {
		await this.authService.logout();
		const state = context.getState();
		context.setState({...state, token: null, user: null});
		await this.pushService.registerPush();

		this.pushService.postBroadcastMessage({ type: AuthState.userChangedMessageType, payload: { token: null } });

		// try {
		// 	localStorage.clear();
		// 	document.cookie.split(";").forEach(function (c) { document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); });
		// }
		// catch { }
		if (location.href) location.href = location.href;
	}
}
